import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiUsers, FiCheck, FiLoader, FiAlertCircle } from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "./JoinByCodeModal.css";

/**
 * JoinByCodeModal - Allows users to join workshops using an invite code
 * 
 * Usage:
 * <JoinByCodeModal 
 *   isOpen={showJoinModal} 
 *   onClose={() => setShowJoinModal(false)} 
 * />
 */
const JoinByCodeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [joinCode, setJoinCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!joinCode.trim()) {
      setError("Please enter an invite code");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await workshopApi.joinByCode(joinCode.trim().toUpperCase());
      
      if (response.success) {
        setSuccess({
          workshopId: response.data.workshopId,
          workshopTitle: response.data.workshopTitle,
          status: response.data.participantStatus,
        });
        toast.success(response.message || "Successfully joined workshop!");
      } else {
        throw new Error(response.message || "Failed to join workshop");
      }
    } catch (err) {
setError(err.message || "Invalid invite code. Please check and try again.");
      toast.error(err.message || "Failed to join workshop");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewWorkshop = () => {
    if (success?.workshopId) {
      navigate(`/workshops/${success.workshopId}`);
      onClose();
    }
  };

  const handleClose = () => {
    setJoinCode("");
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="jbc-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="jbc-modal-content"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="jbc-modal-header">
            <div className="jbc-modal-icon">
              <FiUsers size={24} />
            </div>
            <h2>Join Workshop</h2>
            <button className="jbc-close-btn" onClick={handleClose}>
              <FiX size={20} />
            </button>
          </div>

          {/* Success State */}
          {success ? (
            <div className="jbc-success-state">
              <div className="jbc-success-icon">
                <FiCheck size={32} />
              </div>
              <h3>Welcome!</h3>
              <p>You've successfully joined:</p>
              <p className="jbc-workshop-title">{success.workshopTitle}</p>
              <p className="jbc-status">
                Status: <span className={`status-${success.status}`}>
                  {success.status === "approved" ? "Approved ✓" : "Pending Approval"}
                </span>
              </p>
              <button className="jbc-view-btn" onClick={handleViewWorkshop}>
                View Workshop
              </button>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="jbc-form">
              <p className="jbc-description">
                Enter the invite code shared by the workshop organizer to join instantly.
              </p>

              <div className="jbc-input-wrapper">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => {
                    setJoinCode(e.target.value.toUpperCase());
                    setError(null);
                  }}
                  placeholder="Enter invite code (e.g., ABC123XY)"
                  className={`jbc-code-input ${error ? "jbc-input-error" : ""}`}
                  maxLength={20}
                  autoComplete="off"
                  autoFocus
                />
                {error && (
                  <div className="jbc-error-message">
                    <FiAlertCircle size={14} />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="jbc-actions">
                <button
                  type="button"
                  className="jbc-cancel-btn"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="jbc-submit-btn"
                  disabled={isLoading || !joinCode.trim()}
                >
                  {isLoading ? (
                    <>
                      <FiLoader className="jbc-spinner" size={16} />
                      Joining...
                    </>
                  ) : (
                    "Join Workshop"
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JoinByCodeModal;
